// The data behind the Bulk Review page.
//
// Everything here comes out of one GET api/projects: the customer map already
// carries every survey's name, key, upload date and uploader, and *which list a
// survey sits in is what makes it approved* - under a drillhole means approved,
// in unallocatedSurveys means not. No per-survey S3 read is needed to build the
// list or the headline counts.
//
// Kept pure and separate from the component so the date-window arithmetic is
// testable; `now` is always injected for the same reason.

const MS_PER_DAY = 24 * 60 * 60 * 1000;

// .NET writes a DateTime it never set as 0001-01-01T00:00:00. It is not a date,
// it means "no value", and it must not be read as "approved in the year 1".
const UNSET_DATE = '0001-01-01';

/**
 * Every survey the customer has, allocated or not, as a flat list.
 */
export function flattenSurveys(map) {
    const rows = [];
    if (!map) return rows;

    (map.projects || []).forEach(project => {
        (project.drillholes || []).forEach(drillhole => {
            (drillhole.surveys || []).forEach(reference => {
                rows.push(row(reference, project.name || '', drillhole.name || '', true));
            });
        });
    });
    (map.unallocatedSurveys || []).forEach(reference => {
        rows.push(row(reference, '', '', false));
    });
    return rows;
}

function row(reference, project, drillhole, approved) {
    return {
        fileId: reference.fileId,
        key: reference.key,
        // Identify rows by fileId, never fileName: two surveys can share a name
        // because the tool only suffixes a timestamp for some device types.
        fileName: reference.fileName || '',
        surveyType: reference.surveyType || '',
        project,
        drillhole,
        approved,
        uploaded: reference.uploaded || null,
        uploadedBy: reference.uploadedBy || '',
        // Null on everything approved before these were recorded. That means
        // "approved, date unknown" - never "not approved".
        approvedAt: usableDate(reference.approvedAt) ? reference.approvedAt : null,
        approvedBy: reference.approvedBy || ''
    };
}

function usableDate(value) {
    if (!value) return false;
    if (String(value).startsWith(UNSET_DATE)) return false;
    return !isNaN(new Date(value).getTime());
}

/**
 * Was this date within the last `days` days of `now`?
 *
 * Note both `uploaded` and `approvedAt` are written by the server as a local
 * DateTime with no offset, so the browser reads them in *its* timezone. Against
 * a server in a different zone the day boundary shifts; the page says "server
 * time" rather than pretending otherwise.
 */
export function withinDays(value, days, now) {
    if (!usableDate(value)) return false;
    const diff = now - new Date(value).getTime();
    return diff <= days * MS_PER_DAY;
}

/**
 * Counts for the headline panel.
 *
 * `approvedDateUnknown` is the number of approved surveys with no approval date
 * at all. It is surfaced because otherwise the approved counts silently read as
 * "nothing was approved" for everything predating the stamp.
 */
export function headlineCounts(rows, now) {
    const approved = rows.filter(r => r.approved);
    const count = (list, pick, days) => list.filter(r => withinDays(pick(r), days, now)).length;

    return {
        total: rows.length,
        approvedTotal: approved.length,
        unapprovedTotal: rows.length - approved.length,
        uploaded: {
            day: count(rows, r => r.uploaded, 1),
            week: count(rows, r => r.uploaded, 7),
            month: count(rows, r => r.uploaded, 30)
        },
        approved: {
            day: count(approved, r => r.approvedAt, 1),
            week: count(approved, r => r.approvedAt, 7),
            month: count(approved, r => r.approvedAt, 30)
        },
        approvedDateUnknown: approved.filter(r => !r.approvedAt).length
    };
}

/**
 * The search.
 *
 * mode: 'uploaded' | 'approved' | 'either'.
 *
 * An approved survey with no approval date can never match on approval - there
 * is nothing to match against. It still matches on upload, and the page says how
 * many were skipped so the gap is visible rather than looking like an empty
 * result.
 */
export function filterSurveys(rows, { days, mode }, now) {
    const matchesUpload = r => withinDays(r.uploaded, days, now);
    const matchesApproval = r => r.approved && withinDays(r.approvedAt, days, now);

    let predicate;
    if (mode === 'approved') predicate = matchesApproval;
    else if (mode === 'either') predicate = r => matchesUpload(r) || matchesApproval(r);
    else predicate = matchesUpload;

    return rows.filter(predicate).sort(sortByRecency(mode));
}

// Newest first, on whichever date the search was about.
function sortByRecency(mode) {
    const stamp = r => {
        const value = mode === 'approved' ? r.approvedAt : r.uploaded;
        const time = value ? new Date(value).getTime() : NaN;
        return isNaN(time) ? -Infinity : time;
    };
    return (a, b) => stamp(b) - stamp(a);
}

/**
 * How many approved surveys the search could not consider because they carry no
 * approval date. Zero once everything in the window was approved after the stamp
 * shipped.
 */
export function unmatchableApproved(rows, mode) {
    if (mode === 'uploaded') return 0;
    return rows.filter(r => r.approved && !r.approvedAt).length;
}

/**
 * The rows ticked when a search returns: the approved ones. Unapproved surveys
 * can be exported, but only by the user explicitly asking for them.
 */
export function defaultSelection(rows) {
    return new Set(rows.filter(r => r.approved).map(r => r.fileId));
}
